import { useCallback, useEffect, useMemo, useRef } from 'react';
import { X } from 'shared/icons';
import { TBtn, TInput } from '../primitives';
import { useTokens } from '../tokens';
import { PresetBuilderProvider, PSection, usePresetBuilder } from './context.jsx';
import { ChartSettings } from './sections/ChartSettings.jsx';
import { ColumnSelector } from './sections/ColumnSelector.jsx';
import { FilterSection } from './sections/FilterSection.jsx';
import { FullParams } from './sections/FullParams.jsx';
import { SortConfig } from './sections/SortConfig.jsx';

// Re-export do hook + PSection pro consumo externo (callers que querem ler o
// context fora das sections, ou montar PSections customizadas).
export { PSection, usePresetBuilder };

const CATEGORY_OPTS = ['Análise', 'Estoque', 'Catálogo', 'Pessoal'];

/**
 * Layout default — usado quando o caller NÃO passa children. Mantém 100% o
 * comportamento do antigo PresetBuilder monolítico (ordem das sections, hints,
 * e o block Nome+Categoria no topo).
 */
function DefaultLayout() {
    return (
        <>
            <ChartSettings />
            <FilterSection />
            <FullParams />
            <ColumnSelector />
            <SortConfig />
        </>
    );
}

/**
 * PresetBuilder — Compound Component. Edita um draft de preset em sidebar.
 *
 * Uso compound (custom layout):
 *   <PresetBuilder draft={...} onChange={...} onSave={...} onCancel={...}>
 *     <PresetBuilder.ColumnSelector />
 *     <PresetBuilder.FilterSection />
 *     <PresetBuilder.FullParams />
 *     <PresetBuilder.SortConfig />
 *     <PresetBuilder.ChartSettings />
 *   </PresetBuilder>
 *
 * Uso clássico (sem children — backward compat):
 *   <PresetBuilder draft={...} onChange={...} ... />
 *
 * Props:
 *   draft       — preset draft (objeto plano: { name, category, identifier, columns, filters, sort, ... })
 *   onChange    — (nextDraft) => void
 *   onSave      — () => Promise<void>
 *   onCancel    — () => void
 *   brandOpts   — string[] (opções de marca extraídas do dataset)
 *   tagOpts     — string[] (idem tags)
 *   isNew       — boolean (afeta título + label do botão de salvar)
 *   saving      — boolean (desabilita botões enquanto salva)
 *   canSavePersonal — quando false, esconde categoria "Pessoal" (sem userId disponível)
 */
export function PresetBuilder({
    draft,
    onChange,
    onSave,
    onCancel,
    brandOpts,
    tagOpts,
    warehouses = [],
    isNew,
    saving,
    canSavePersonal = true,
    customColumns = [],
    customColumnsLoading = false,
    onCreateCustomColumn,
    onUpdateCustomColumn,
    onDeleteCustomColumn,
    children,
}) {
    const T = useTokens();

    // Map<id, def> derivado da lista de customColumns pro lookup do ColumnSelector
    // (mostra nome no chip + resolve o def pro editor). useMemo previne rebuild
    // a cada render do builder.
    const customColumnsById = useMemo(() => {
        const map = new Map();
        for (const c of customColumns) map.set(c.id, c);
        return map;
    }, [customColumns]);

    const patch = useCallback((updates) => onChange({ ...draft, ...updates }), [draft, onChange]);
    const patchFilters = useCallback((nextFilters) => onChange({ ...draft, filters: nextFilters }), [draft, onChange]);

    // Auto-foco no nome quando entra em modo "novo" (campo vazio).
    const nameRef = useRef(null);
    useEffect(() => {
        if (isNew && nameRef.current) {
            nameRef.current.focus?.();
            nameRef.current.select?.();
        }
    }, [isNew]);

    const cols = Array.isArray(draft?.columns) ? draft.columns : [];
    const canSave = Boolean(draft?.name?.trim()) && cols.length > 0;
    const categories = canSavePersonal ? CATEGORY_OPTS : CATEGORY_OPTS.filter((c) => c !== 'Pessoal');

    // Context disponibilizado pras sections — agrupa tudo que cada section
    // precisa (draft, callbacks específicos, opts e custom column wiring).
    const ctxValue = useMemo(
        () => ({
            draft,
            onChange,
            patch,
            patchFilters,
            brandOpts,
            tagOpts,
            warehouses,
            customColumns,
            customColumnsById,
            customColumnsLoading,
            onCreateCustomColumn,
            onUpdateCustomColumn,
            onDeleteCustomColumn,
        }),
        [
            draft,
            onChange,
            patch,
            patchFilters,
            brandOpts,
            tagOpts,
            warehouses,
            customColumns,
            customColumnsById,
            customColumnsLoading,
            onCreateCustomColumn,
            onUpdateCustomColumn,
            onDeleteCustomColumn,
        ],
    );

    return (
        <PresetBuilderProvider value={ctxValue}>
            <div
                data-faid="Preset_Builder_Panel_Operacoes"
                style={{
                    width: '100%',
                    height: '100%',
                    background: T.bg,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    // Sem borderLeft — o Splitter (linha cinza arrastável) já separa
                    // visualmente do painel da tabela. Borda extra era ruído.
                }}
            >
                {/* Header usa bgMid+ink+borderBottom em vez de ink+#fff hardcoded.
                    Antes: em temas escuros `T.ink` é cinza-claro (#f5f5f5) → header
                    branco com texto branco = invisível. Agora adapta a cada tema. */}
                <div
                    style={{
                        padding: '10px 14px',
                        borderBottom: `1px solid ${T.lineMid}`,
                        background: T.bgMid,
                        color: T.ink,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexShrink: 0,
                    }}
                >
                    <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: T.ink }}>
                        {isNew ? 'Novo preset' : 'Editar preset'}
                    </span>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Fechar editor de preset"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: T.muted,
                            display: 'inline-flex',
                            padding: 4,
                            outline: 'none',
                        }}
                    >
                        <X size={14} />
                    </button>
                </div>

                {/* scrollbar-gutter:stable reserva espaço fixo pra scrollbar mesmo
                    quando não aparece — sem isso, o conteúdo "pula" lateralmente
                    ao adicionar colunas/abrir editor (transição com→sem scroll). */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: 14,
                        scrollbarGutter: 'stable',
                    }}
                >
                    {/* Nome + Categoria lado a lado pra economizar altura vertical.
                        Nome flex (cresce), Categoria fixo (160px) por ser select compacto.
                        Bloco fora das sections — sempre renderiza, independente de
                        children passados (são metadata core do preset). */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: 0.6,
                                    textTransform: 'uppercase',
                                    color: T.muted,
                                    marginBottom: 6,
                                }}
                            >
                                Nome
                            </div>
                            <TInput
                                value={draft?.name || ''}
                                onChange={(v) => patch({ name: v })}
                                placeholder="Nome do preset"
                                width="100%"
                            />
                        </div>
                        <div style={{ width: 160, flexShrink: 0 }}>
                            <div
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: 0.6,
                                    textTransform: 'uppercase',
                                    color: T.muted,
                                    marginBottom: 6,
                                }}
                            >
                                Categoria
                            </div>
                            <select
                                value={draft?.category || 'Análise'}
                                onChange={(e) => patch({ category: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    borderRadius: 4,
                                    border: `1px solid ${T.line}`,
                                    background: T.bg,
                                    color: T.ink,
                                    fontSize: 13,
                                    outline: 'none',
                                    height: 32,
                                }}
                            >
                                {categories.map((c) => (
                                    <option key={c} value={c}>
                                        {c === 'Pessoal' ? 'Pessoal (só você vê)' : c}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Compound: se children, deixa o caller compor; senão renderiza
                        o layout default (todas as sections na ordem original). */}
                    {children || <DefaultLayout />}
                </div>

                <div
                    style={{
                        padding: 12,
                        borderTop: `1px solid ${T.line}`,
                        display: 'flex',
                        gap: 6,
                        flexShrink: 0,
                    }}
                >
                    <TBtn
                        size="sm"
                        variant="ghost"
                        onClick={onCancel}
                        disabled={saving}
                        data-faid="Discard_Button_Preset_Builder"
                    >
                        Descartar
                    </TBtn>
                    <div style={{ flex: 1 }} />
                    <TBtn
                        size="sm"
                        variant="primary"
                        onClick={onSave}
                        disabled={!canSave || saving}
                        title={!canSave ? 'Defina nome e ao menos 1 coluna' : undefined}
                        data-faid="Save_Button_Preset_Builder"
                    >
                        {saving ? 'Salvando…' : 'Salvar preset'}
                    </TBtn>
                </div>
            </div>
        </PresetBuilderProvider>
    );
}

// Compound API — namespaced subcomponents pro uso `<PresetBuilder.X />`.
PresetBuilder.ColumnSelector = ColumnSelector;
PresetBuilder.FilterSection = FilterSection;
PresetBuilder.FullParams = FullParams;
PresetBuilder.SortConfig = SortConfig;
PresetBuilder.ChartSettings = ChartSettings;
